<template lang="pug">
</template>

<script>
import axios from 'axios'

/**
 * YzDatabinSync - A renderless component for databin synchronization
 *
 * This component provides centralized databin state management that automatically
 * handles lifecycle, reactivity, and cleanup without requiring manual intervention.
 *
 * Features:
 * - Automatic lifecycle management (creation, polling, cleanup)
 * - Vue reactivity integration
 * - Optimistic updates with rollback on error
 * - Race condition prevention
 * - Memory leak prevention through automatic cleanup
 *
 * Usage:
 * <yz-databin-sync
 *   v-model="databinItems"
 *   :ttl="900"
 *   :polling-interval="4000"
 *   :auto-start="true"
 *   @databin-created="handleDatabinCreated"
 *   @error="handleError"
 *   ref="databinSync"
 * />
 *
 * // Access methods via ref
 * this.$refs.databinSync.addItems(uploadResults)
 * this.$refs.databinSync.removeItems(itemsToRemove)
 */
export default {
  name: 'YzDatabinSync',
  props: {
    // v-model support
    value: {
      type: Array,
      default: () => []
    },
    addedItems: {
      type: Array,
      default: () => []
    },
    // Required databin key
    databinKey: {
      type: String,
      required: true
    },
    // Configuration
    ttl: {
      type: Number,
      default: 900
    },
    pollingInterval: {
      type: Number,
      default: 4000
    },
    databinUrl: {
      type: String,
      default: '/databin/'
    },
    databinMethod: {
      type: String,
      default: 'PUT'
    },
    databinHeaders: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      databinItems: [],
      polling: null,
      isPolling: false,
      hasInitialized: false,
      pendingOperations: new Set(),
      lastSyncTimestamp: null
    }
  },
  computed: {
    finalDatabinUrl() {
      if (!this.databinKey) return null
      return this.databinUrl + this.databinKey + '/?ttl=' + this.ttl
    },
    // Merge addedItems with databinItems that are NOT in addedItems
    mergedItems() {
      // Start with all addedItems
      const combined = [...(this.addedItems || [])]

      // Add databinItems that are NOT already in addedItems
      const databinItemsNotInAdded = this.databinItems.filter(databinItem => {
        return !this.addedItems.some(addedItem =>
          this.isSameItem(addedItem, databinItem)
        )
      })

      // Combine them
      return [...combined, ...databinItemsNotInAdded]
    }
  },
  watch: {
    // Emit v-model updates using merged items
    databinItems: {
      handler() {
        this.$emit('input', [...this.mergedItems])
      },
      deep: true
    },
    // Watch addedItems changes to update merged value
    addedItems: {
      handler() {
        this.$emit('input', [...this.mergedItems])
      },
      deep: true
    },
    // Watch for external v-model changes
    value: {
      handler(newValue) {
        if (JSON.stringify(newValue) !== JSON.stringify(this.mergedItems)) {
          // Update databinItems based on the new value
          // Filter out addedItems to get pure databinItems
          const pureDatabin = newValue.filter(item => {
            return !this.addedItems.some(addedItem => this.isSameItem(item, addedItem))
          })
          this.databinItems = [...pureDatabin]
        }
      },
      deep: true
    },
    // Watch for databinKey changes
    databinKey: {
      handler(newKey) {
        if (newKey && !this.hasInitialized) {
          this.initialize()
        }
      },
      immediate: true
    }
  },
  mounted() {
    // Initialize databinItems with prop value, filtering out addedItems
    if (this.value && this.value.length > 0) {
      const pureDatabin = this.value.filter(item => {
        return !this.addedItems.some(addedItem => this.isSameItem(item, addedItem))
      })
      this.databinItems = [...pureDatabin]
    }
  },
  beforeDestroy() {
    // Automatic cleanup prevents memory leaks
    this.stopPolling()
    this.pendingOperations.clear()
    this.databinItems = []
    this.hasInitialized = false
    this.lastSyncTimestamp = null
  },
  methods: {
    // Helper to compare items
    isSameItem(item1, item2) {
      return (item1.hid && item2.hid && item1.hid === item2.hid) || (item1.fileId && item2.fileId && item1.fileId === item2.fileId)
    },

    async refreshFromDatabin() {
      if (!this.databinKey) return

      // Allow refresh even during pending operations to get latest state
      try {
        const response = await axios.get(`/databin/${this.databinKey}`)

        if (response.data && JSON.stringify(response.data) !== '{}') {
          const serverItems = Array.isArray(response.data) ? response.data : []

          // Only update if server data is different (avoid infinite loops)
          if (JSON.stringify(serverItems) !== JSON.stringify(this.databinItems)) {
            this.databinItems = serverItems
            this.lastSyncTimestamp = new Date().toISOString()
          }
        }
      } catch (error) {
        const errorMessage = error.response?.status === 404 ? 'Databin expired or not found' : 'Failed to refresh from databin'
        this.$emit('error', { message: errorMessage, error })
      }
    },

    async syncToDatabin() {
      if (!this.databinKey) return

      try {
        const headers = {
          'Content-Type': 'application/json',
          ...this.databinHeaders
        }

        const response = await axios({
          method: this.databinMethod,
          url: this.finalDatabinUrl,
          data: this.databinItems,
          headers
        })

        this.lastSyncTimestamp = new Date().toISOString()

        // Handle response data if needed
        if (response.data && response.data.data) {
          // Update local items with server response if different
          if (JSON.stringify(response.data.data) !== JSON.stringify(this.databinItems)) {
            this.databinItems = response.data.data
          }
        }
      } catch (error) {
        const errorMessage = 'Failed to sync to databin'
        this.$emit('error', { message: errorMessage, error })
        // Don't throw error to avoid breaking the sync flow
      }
    },

    // Polling management
    startPolling() {
      this.stopPolling()
      this.isPolling = true
      this.polling = setInterval(() => {
        this.refreshFromDatabin()
      }, this.pollingInterval)
    },

    stopPolling() {
      if (this.polling) {
        clearInterval(this.polling)
        this.polling = null
        this.isPolling = false
      }
    },

    // Public API
    async initialize() {
      if (this.hasInitialized || !this.databinKey) return

      try {
        // Initialize with current items if any
        if (this.databinItems.length > 0) {
          await this.syncToDatabin()
        } else {
          await this.refreshFromDatabin()
        }

        this.startPolling()
        this.hasInitialized = true
      } catch (error) {
        const errorMessage = 'Failed to initialize databin sync'
        this.$emit('error', { message: errorMessage, error })
      }
    },

    // Item management with optimistic updates
    async addItems(items) {
      const itemsArray = Array.isArray(items) ? items : [items]
      const operationId = `add_${Date.now()}`

      try {
        this.pendingOperations.add(operationId)

        // Optimistic update
        this.databinItems = [...this.databinItems, ...itemsArray]

        // Sync to databin
        await this.syncToDatabin()
      } catch (error) {
        // Rollback on error
        this.databinItems = this.databinItems.filter(item => !itemsArray.some(newItem => this.isSameItem(item, newItem)))
        const errorMessage = 'Failed to add items to databin'
        this.$emit('error', { message: errorMessage, error })
      } finally {
        this.pendingOperations.delete(operationId)
      }
    },

    async removeItems(items) {
      const itemsArray = Array.isArray(items) ? items : [items]
      const operationId = `remove_${Date.now()}`

      // Store original state for rollback
      const originalItems = [...this.databinItems]
      const originalAddedItems = [...this.addedItems]

      try {
        this.pendingOperations.add(operationId)

        // Optimistic update - remove from databinItems
        this.databinItems = this.databinItems.filter(item => !itemsArray.some(removeItem => this.isSameItem(item, removeItem)))

        // Also remove matching items from addedItems and emit update
        const filteredAddedItems = this.addedItems.filter(item => !itemsArray.some(removeItem => this.isSameItem(item, removeItem)))
        this.$emit('update:addedItems', filteredAddedItems)

        // Sync to databin
        await this.syncToDatabin()
      } catch (error) {
        // Rollback on error
        this.databinItems = originalItems
        this.$emit('update:addedItems', originalAddedItems)
        const errorMessage = 'Failed to remove items from databin'
        this.$emit('error', { message: errorMessage, error })
      } finally {
        this.pendingOperations.delete(operationId)
      }
    },

    async updateItems(items) {
      const itemsArray = Array.isArray(items) ? items : [items]
      const operationId = `update_${Date.now()}`

      // Store original state for rollback
      const originalItems = [...this.databinItems]

      try {
        this.pendingOperations.add(operationId)

        // Optimistic update
        itemsArray.forEach(updateItem => {
          const index = this.databinItems.findIndex(item => this.isSameItem(item, updateItem))
          if (index !== -1) {
            this.databinItems[index] = { ...this.databinItems[index], ...updateItem }
          }
        })

        // Sync to databin
        await this.syncToDatabin()
      } catch (error) {
        // Rollback on error
        this.databinItems = originalItems
        const errorMessage = 'Failed to update items in databin'
        this.$emit('error', { message: errorMessage, error })
      } finally {
        this.pendingOperations.delete(operationId)
      }
    },

    // Manual operations
    async manualSync() {
      await this.refreshFromDatabin()
    },

    async forceSync() {
      await this.syncToDatabin()
    },

    // Getters for external access
    getDatabinKey() {
      return this.databinKey
    },

    getIsPolling() {
      return this.isPolling
    },

    getLastSync() {
      return this.lastSyncTimestamp
    },

    getPendingOperations() {
      return this.pendingOperations.size
    }
  }
}
</script>

<style scoped></style>